/**
 * Application constants for dropdowns, options, and configuration values.
 */

/**
 * Fallback brand used before API response is available or when brand API fails.
 * In non-prod, this is intentionally fail-loud to surface configuration issues.
 */
export const DEFAULT_BRAND = import.meta.env.PROD
  ? {
      id: 'cngc',
      code: 'CNGC',
      name: 'Cascade Natural Gas',
      shortName: 'cng',
      longName: 'Cascade Natural Gas Corporation',
      theme: 'cng',
      logoUrl: '/assist/brand/cng/images/logo.svg',
      sealScriptUrl: 'https://seal.godaddy.com/getSeal?sealID=iwBIhTZAukVwXD7BMgnLrFMa65eOrZfwSIDgBDOF1BLvDc3uIzwyU3zhvUkl',
    }
  : {
      id: 'unknown',
      code: 'UNKNOWN',
      name: 'Unknown Brand',
      shortName: 'unknown',
      longName: 'Unknown Brand (Fallback)',
      theme: 'cng',
      logoUrl: '/assist/brand/cng/images/logo.svg',
      sealScriptUrl: '',
    };


/**
 * State abbreviations.
 */
export const STATES = {
  WA: 'WA',
  OR: 'OR',
};

/**
 * Fund type string values for comparisons.
 */
export const FUND_TYPES = {
  LIHEAP: 'LIHEAP',
  CARES: 'CARES',
  PPC: 'PPC',
  EDP: 'EDP',
  WINTERHELP: 'WINTERHELP',
  OTHER: 'OTHER',
};

/**
 * CARES type string values for comparisons.
 */
export const CARES_TYPE_VALUES = {
  NEWDISCOUNT: 'NEWDISCOUNT',
  NEWDISCOUNTANDGRANT: 'NEWDISCOUNTANDGRANT',
  CHANGEDISCOUNT: 'CHANGEDISCOUNT',
  CHANGEDISCOUNTANDNEWGRANT: 'CHANGEDISCOUNTANDNEWGRANT',
  NEWGRANT: 'NEWGRANT',
  EXCEPTIONREQUEST: 'EXCEPTIONREQUEST',
};

/**
 * CARES types that include a grant component.
 */
export const CARES_GRANT_OPTIONS = [
  CARES_TYPE_VALUES.NEWGRANT,
  CARES_TYPE_VALUES.NEWDISCOUNTANDGRANT,
  CARES_TYPE_VALUES.CHANGEDISCOUNTANDNEWGRANT,
  CARES_TYPE_VALUES.EXCEPTIONREQUEST,
];

/**
 * Page status string values for API responses.
 */
export const PAGE_STATUS = {
  SUCCESS: 'SUCCESS',
  FAIL: 'FAIL',
  WARN: 'WARN',
};

/**
 * Benefit status string values for comparisons.
 */
export const BENEFIT_STATUS = {
  APPROVED: 'APPROVED',
  AUTOAPPROVED: 'AUTOAPPROVED',
  REJECTED: 'REJECTED',
  HELD: 'HELD',
  AUTOHELD: 'AUTOHELD',
  CREATED: 'CREATED',
};

/**
 * User type values for role-based access control.
 */
export const USER_TYPES = {
  USER: 10,
  ADMIN: 20,
  USER_MANAGER: 30,
  MASTER_ADMIN: 40,
};

/**
 * Fund type options for benefit forms.
 */
export const BENEFIT_FUND_TYPES = [
  { value: FUND_TYPES.LIHEAP, label: FUND_TYPES.LIHEAP },
  { value: FUND_TYPES.CARES, label: FUND_TYPES.CARES },
  { value: FUND_TYPES.PPC, label: 'OLIBA' },
  { value: FUND_TYPES.WINTERHELP, label: FUND_TYPES.WINTERHELP },
  { value: FUND_TYPES.OTHER, label: FUND_TYPES.OTHER },
];

/**
 * Fund type options for customer contact forms.
 * Types with `states` array are filtered by customer state.
 * Types without `states` are available in all states.
 */
export const CONTACT_FUND_TYPES = [
  { value: FUND_TYPES.CARES, label: FUND_TYPES.CARES, states: [STATES.WA] },
  { value: FUND_TYPES.LIHEAP, label: FUND_TYPES.LIHEAP },
  { value: FUND_TYPES.EDP, label: FUND_TYPES.EDP, states: [STATES.OR] },
  { value: FUND_TYPES.PPC, label: 'OLIBA', states: [STATES.OR] },
  { value: FUND_TYPES.OTHER, label: 'Other' },
];

/**
 * CARES type options for benefit applications.
 */
export const CARES_TYPES = [
  { value: CARES_TYPE_VALUES.NEWDISCOUNT, label: 'Apply for new discount only' },
  { value: CARES_TYPE_VALUES.NEWDISCOUNTANDGRANT, label: 'Apply for new discount and grant' },
  { value: CARES_TYPE_VALUES.CHANGEDISCOUNT, label: 'Change existing discount only' },
  { value: CARES_TYPE_VALUES.CHANGEDISCOUNTANDNEWGRANT, label: 'Change existing discount and apply for grant' },
  { value: CARES_TYPE_VALUES.NEWGRANT, label: 'Apply for grant only' },
  { value: CARES_TYPE_VALUES.EXCEPTIONREQUEST, label: 'Apply for exception request' },
];

/**
 * Referral source options for benefit applications.
 */
export const REFERRAL_SOURCES = [
  { value: '', label: 'Select...' },
  { value: 'LOCAL_COMMUNITY_ACTION_AGENCY', label: 'Local Community Action Agency' },
  { value: 'COMMUNITY_BASED_ORGANIZATION', label: 'Community-Based Organization' },
  { value: 'CASCADE_CUSTOMER_SERVICE', label: 'Cascade touchpoint: Customer Service' },
  { value: 'CASCADE_MAIL', label: 'Cascade touchpoint: Mail' },
  { value: 'CASCADE_WEBSITE', label: 'Cascade touchpoint: Website' },
  { value: 'CASCADE_SOCIAL_MEDIA', label: 'Cascade touchpoint: Social Media' },
  { value: 'CASCADE_EMAIL', label: 'Cascade touchpoint: Email' },
  { value: 'CASCADE_BILL_INSERT', label: 'Cascade touchpoint: Bill Insert/Message' },
  { value: 'CASCADE_AUTOMATED_CALL', label: 'Cascade touchpoint: Automated Call' },
  { value: 'CASCADE_DOOR_TAG', label: 'Cascade touchpoint: Door Tag' },
  { value: 'WEBSITE_ADS', label: 'Non-Cascade advertising: Website ads' },
  { value: 'GOOGLE_ADS', label: 'Non-Cascade advertising: Google ads' },
  { value: 'STREAMING', label: 'Non-Cascade advertising: Streaming' },
  { value: 'WORD_OF_MOUTH', label: 'Word of Mouth' },
  { value: 'UNKNOWN', label: 'Unknown' },
];

export const getReferralSourceLabel = (referralSource) => {
  const match = REFERRAL_SOURCES.find(
    rs => rs.value === referralSource
  );

  return match?.label ?? referralSource;
};

/**
 * Note type string values for comparisons.
 */
export const NOTE_TYPE_VALUES = {
  APPOINTMENT_DATE: 'APPOINTMENT_DATE',
  APPLICATION_IN_PROGRESS: 'APPLICATION_IN_PROGRESS',
  APPOINTMENT_CANCELED: 'APPOINTMENT_CANCELED',
};

/**
 * Note type options for customer contact forms.
 */
export const NOTE_TYPES = [
  { value: NOTE_TYPE_VALUES.APPOINTMENT_DATE, label: 'Confirmed Appointment Date' },
  { value: NOTE_TYPE_VALUES.APPLICATION_IN_PROGRESS, label: 'Application In Progress' },
  { value: NOTE_TYPE_VALUES.APPOINTMENT_CANCELED, label: 'Application Cancelled' },
];

/**
 * Validation regex patterns.
 */
export const USERNAME_REGEX = /^[a-zA-Z][a-zA-Z0-9.]*$/;
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Income measurement limits (percentages).
 */
export const MAX_AMI = 80;
export const MAX_SMI = 60;
export const MAX_FPL_OR = 150;
export const MAX_FPL_WA = 200;
export const MAX_NUMBER = 999999999;
export const MIN_HOUSEHOLD_SIZE = 1;
export const MAX_HOUSEHOLD_SIZE = 50;

/**
 * Pledge amount limits (dollars).
 */
export const MIN_PLEDGE = 1;
export const MAX_OTHER_PLEDGE = 1000;
export const MAX_WINTER_HELP_PLEDGE = 300;

/**
 * CARES program limits.
 */
export const MAX_CARES_GRANTS = 2;
export const MAX_CARES_AMOUNT = 500;

export const MIN_TPA_MONTHS = 1;
export const MAX_TPA_MONTHS = 24;

/**
 * Income Measuremeant Strings
 */
export const AMI = "AMI";
export const FPL = "FPL";
export const SMI = "SMI";
export const AMI_DESCR = "Area Median Income";
export const FPL_DESCR = "Federal Poverty Level";
export const SMI_DESCR = "State Median Income";

export const INCOME_MEASUREMENT_TYPES = [
  { value: AMI, label: AMI_DESCR, states: [STATES.WA] },
  { value: FPL, label: FPL_DESCR },
  { value: SMI, label: SMI_DESCR, states: [STATES.OR] },
];